import { memo, useRef } from 'react';
import { AnimatePresence, Reorder, motion, useDragControls, useReducedMotion } from 'framer-motion';
import { AlertCircle, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Clock, GripVertical, Loader2, X } from 'lucide-react';
import { microInteractions, springs } from '../../../utils/animations';
import { haptics } from '../../../utils/haptics';
import { TONE_CLASSES } from '../../../utils/tones';
import DispatchProgressOverlay from '../dashboard/DispatchProgressOverlay';
import { DELIVERY_COPY, moveEarlierLabel, moveLaterLabel, removeLabel, slideCaption, tileName } from './playlistCopy';

const LONG_PRESS_MS = 300;
const LONG_PRESS_SLOP_PX = 8;

const LIFTED = { scale: 1.04, boxShadow: '0 8px 40px rgba(0,0,0,0.12)', zIndex: 20 };

function Thumbnail({ item, className, hidden }) {
  return (
    <div
      className={`relative overflow-hidden squircle-sm bg-gray-100 dark:bg-elevated-dark-1 ${className}`}
      style={hidden ? { visibility: 'hidden' } : undefined}
      data-flight-target={item.itemKey}
    >
      {item.thumbnailUrl ? (
        <img src={item.thumbnailUrl} alt="" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-300 dark:text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
    </div>
  );
}

// T-1, T-3 … T-6: the caption that replaces the slide count during delivery.
const STATUS_CAPTION = {
  queued: { Icon: Clock, text: DELIVERY_COPY.waiting, tone: 'muted' },
  sent: { Icon: Check, text: DELIVERY_COPY.sent, tone: 'success' },
  failed: { Icon: AlertCircle, text: DELIVERY_COPY.couldntSend, tone: 'error' },
  gone: { Icon: AlertCircle, text: DELIVERY_COPY.gone, tone: 'error' },
  missing: { Icon: AlertCircle, text: DELIVERY_COPY.missing, tone: 'error' },
};

function Caption({ item, status, onRemoveGone }) {
  const entry = STATUS_CAPTION[status];
  if (!entry) {
    return <span className="truncate text-gray-500 dark:text-gray-400">{slideCaption(item.pageCount)}</span>;
  }
  const { Icon, text, tone } = entry;
  return (
    <span className={`flex items-center gap-1 min-w-0 ${TONE_CLASSES[tone]}`} data-testid="playlist-item-status">
      <Icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2.5} aria-hidden="true" />
      <span className="truncate" title={text}>{text}</span>
      {status === 'gone' && onRemoveGone && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onRemoveGone();
          }}
          onPointerDown={(event) => event.stopPropagation()}
          className="ml-auto flex-shrink-0 px-1.5 -my-0.5 rounded font-semibold underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          data-testid="playlist-item-remove-gone"
        >
          {DELIVERY_COPY.remove}
        </button>
      )}
    </span>
  );
}

// F-2 / D-9: on a tile, Remove sits on the thumbnail so the T-5 caption stays whole.
function RemoveGonePill({ onRemove }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onRemove();
      }}
      onPointerDown={(event) => event.stopPropagation()}
      className="absolute z-20 bottom-1.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-600 text-white text-caption font-semibold shadow-soft hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
      data-testid="playlist-item-remove-gone"
    >
      <X className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden="true" />
      {DELIVERY_COPY.remove}
    </button>
  );
}

// The DesignCard "Sent!" disc at tile scale (spec §5.4), cascading on success.
function SentDisc({ delay, small }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.span
      initial={reduceMotion ? { opacity: 0 } : { scale: 0 }}
      animate={reduceMotion ? { opacity: 1 } : { scale: 1 }}
      transition={reduceMotion ? { duration: 0.15 } : { type: 'spring', stiffness: 200, damping: 10, delay }}
      className={`absolute z-10 rounded-full bg-emerald-500 text-white shadow-soft flex items-center justify-center ring-2 ring-white/90 dark:ring-elevated-dark-2 ${
        small ? 'bottom-1 right-1 w-5 h-5' : 'top-1 right-1 w-7 h-7'
      }`}
      data-testid="playlist-item-check"
      aria-hidden="true"
    >
      <Check className={small ? 'w-3 h-3' : 'w-4 h-4'} strokeWidth={3} />
    </motion.span>
  );
}

function PositionBadge({ position, className = '' }) {
  return (
    <span
      className={`absolute top-1 left-1 z-10 w-6 h-6 rounded-full bg-black/70 text-white text-caption font-semibold flex items-center justify-center overflow-hidden ${className}`}
      aria-hidden="true"
    >
      <motion.span
        key={position}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
      >
        {position}
      </motion.span>
    </span>
  );
}

/**
 * One playlist entry. `layout="tile"` is the desktop strip card,
 * `layout="row"` the phone sheet row; both carry the same test ids.
 * The focusable `role="group"` element is the single Tab stop; its buttons
 * are `tabIndex=-1` and mirrored by the arrow/Home/End/Delete keys.
 */
function PlaylistItemTile({
  item,
  position,
  total,
  layout = 'tile',
  status = 'idle',
  progressByAgent,
  agents,
  locked = false,
  isArriving = false,
  checkDelay = 0,
  onRemoveGone,
  onMove,
  onRemove,
  onKeyDown,
  onDragStart,
  onDragEnd,
  onDrag,
  describedBy,
}) {
  const isRow = layout === 'row';
  const dragControls = useDragControls();
  const pressRef = useRef(null);
  const isFirst = position === 1;
  const isLast = position === total;

  const cancelPress = () => {
    if (pressRef.current) clearTimeout(pressRef.current.timer);
    pressRef.current = null;
  };

  const startHandlePress = (event) => {
    if (locked) return;
    if (event.pointerType === 'mouse') {
      dragControls.start(event);
      return;
    }
    const origin = { x: event.clientX, y: event.clientY };
    const timer = setTimeout(() => {
      haptics.medium();
      dragControls.start(event);
      pressRef.current = null;
    }, LONG_PRESS_MS);
    pressRef.current = { timer, origin };
  };

  const trackHandlePress = (event) => {
    const press = pressRef.current;
    if (!press) return;
    if (Math.hypot(event.clientX - press.origin.x, event.clientY - press.origin.y) > LONG_PRESS_SLOP_PX) cancelPress();
  };

  const iconButton = (testId, label, Icon, handler, disabled) => (
    <button
      type="button"
      tabIndex={-1}
      data-testid={testId}
      aria-label={label}
      title={label}
      disabled={disabled || locked}
      onClick={(event) => {
        event.stopPropagation();
        handler(event);
      }}
      onPointerDown={(event) => event.stopPropagation()}
      className={`group/btn flex items-center justify-center flex-shrink-0 disabled:cursor-not-allowed focus-visible:outline-none ${
        isRow ? 'w-11 h-11' : 'w-[38px] h-[38px]'
      }`}
    >
      <span
        className={`flex items-center justify-center rounded-full transition-colors group-disabled/btn:opacity-40 group-focus-visible/btn:ring-2 group-focus-visible/btn:ring-primary-500 ${
          isRow
            ? 'w-9 h-9 text-gray-600 dark:text-gray-300 group-hover/btn:bg-gray-100 dark:group-hover/btn:bg-elevated-dark-3'
            : 'w-8 h-8 text-white group-hover/btn:bg-white/20'
        }`}
      >
        <Icon className="w-4 h-4" strokeWidth={2.25} />
      </span>
    </button>
  );

  const controls = (
    <>
      {iconButton('playlist-item-move-left', moveEarlierLabel(item.title), isRow ? ChevronUp : ChevronLeft, () => onMove(item.itemKey, -1, 'left'), isFirst)}
      {iconButton('playlist-item-move-right', moveLaterLabel(item.title), isRow ? ChevronDown : ChevronRight, () => onMove(item.itemKey, 1, 'right'), isLast)}
      {iconButton('playlist-item-remove', removeLabel(item.title), X, () => onRemove(item.itemKey))}
    </>
  );

  const showOverlay = (status === 'sending' || status === 'failed') && progressByAgent;
  const dimmed = status === 'queued';
  const alarmed = ['failed', 'gone', 'missing'].includes(status);
  const removeGone = onRemoveGone ? () => onRemoveGone(item.itemKey) : undefined;
  const statusLayer = (small) => (
    <AnimatePresence>
      {dimmed && (
        <motion.span
          key="queued"
          {...microInteractions.fadeIn}
          className={`absolute z-10 rounded-full bg-black/70 text-white flex items-center justify-center ${small ? 'bottom-1 right-1 w-5 h-5' : 'top-1 right-1 w-6 h-6'}`}
          aria-hidden="true"
        >
          <Clock className="w-3.5 h-3.5" strokeWidth={2.5} />
        </motion.span>
      )}
      {status === 'sent' && <SentDisc key="sent" delay={checkDelay} small={small} />}
    </AnimatePresence>
  );

  const body = isRow ? (
    <div className="flex items-center gap-3 h-[72px] pl-2 pr-1">
      <div className="relative flex-shrink-0">
        <Thumbnail
          item={item}
          className={`w-[96px] h-[54px] transition-opacity duration-200 ${dimmed ? 'opacity-60' : ''} ${alarmed ? 'ring-2 ring-red-500' : ''}`}
          hidden={isArriving}
        />
        <PositionBadge position={position} className="!w-5 !h-5 !text-[11px]" />
        {status === 'sending' && (
          <span className="absolute inset-0 z-10 squircle-sm bg-black/70 flex items-center justify-center" aria-hidden="true">
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          </span>
        )}
        {statusLayer(true)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-body-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={item.title}>{item.title}</p>
        <div className="flex items-center -my-1">
          <p className="flex-1 min-w-0 flex text-caption"><Caption item={item} status={status} onRemoveGone={removeGone} /></p>
          <div className="flex items-center">{controls}</div>
        </div>
      </div>
      <div
        aria-hidden="true"
        onPointerDown={startHandlePress}
        onPointerMove={trackHandlePress}
        onPointerUp={cancelPress}
        onPointerCancel={cancelPress}
        className={`flex items-center justify-center w-8 h-11 flex-shrink-0 text-gray-400 dark:text-gray-500 ${locked ? '' : 'cursor-grab active:cursor-grabbing'}`}
        style={{ touchAction: 'none' }}
        data-testid="playlist-item-handle"
      >
        <GripVertical className="w-5 h-5" />
      </div>
    </div>
  ) : (
    <div className="p-1.5">
      <div className="relative group/thumb">
        <Thumbnail
          item={item}
          className={`w-full aspect-video transition-opacity duration-200 ${dimmed ? 'opacity-60' : ''} ${alarmed ? 'ring-2 ring-red-500' : ''}`}
          hidden={isArriving}
        />
        <PositionBadge position={position} />
        {statusLayer(false)}
        {status === 'gone' && removeGone && <RemoveGonePill onRemove={removeGone} />}
        {!locked && status === 'idle' && (
          <GripVertical
            className="absolute top-1 right-1 w-4 h-4 text-white drop-shadow opacity-0 group-hover/tile:opacity-100 transition-opacity"
            aria-hidden="true"
          />
        )}
        {showOverlay && <DispatchProgressOverlay progressByAgent={progressByAgent} agents={agents} />}
        <div className="absolute inset-x-0 bottom-0 flex justify-center pt-5 pb-0.5 rounded-b-[12px] bg-gradient-to-t from-black/75 via-black/40 to-transparent opacity-0 transition-opacity group-hover/tile:opacity-100 group-focus-within/tile:opacity-100 [@media(hover:none)]:opacity-100">
          {controls}
        </div>
      </div>
      <p className="mt-1.5 h-[38px] text-body-sm leading-snug font-medium text-gray-900 dark:text-gray-100 line-clamp-2" title={item.title}>
        {item.title}
      </p>
      <p className="mt-0.5 h-[18px] flex text-caption"><Caption item={item} status={status} /></p>
    </div>
  );

  return (
    <Reorder.Item
      as="li"
      value={item}
      id={item.itemKey}
      dragListener={!isRow && !locked}
      dragControls={dragControls}
      onDragStart={() => onDragStart?.(item)}
      onDrag={(event) => onDrag?.(event)}
      onDragEnd={() => onDragEnd?.(item)}
      layout="position"
      transition={springs.snappy}
      whileDrag={LIFTED}
      className={isRow ? 'relative isolate bg-white dark:bg-elevated-dark-2' : 'relative isolate flex-shrink-0 snap-start list-none'}
      style={{ position: 'relative' }}
    >
      <motion.div
        {...microInteractions.checkboxPop}
        role="group"
        tabIndex={0}
        aria-roledescription="reorderable item"
        aria-label={tileName(item, position, total)}
        aria-describedby={describedBy}
        onKeyDown={onKeyDown}
        data-testid="playlist-item"
        data-item-key={item.itemKey}
        data-position={position}
        data-design-id={item.designId}
        data-status={status}
        className={`group/tile outline-none select-none ${
          isRow
            ? 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500'
            : `w-[128px] xl:w-[152px] squircle-sm bg-white dark:bg-elevated-dark-2 border border-gray-200 dark:border-white/5 shadow-soft-sm focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-elevated-dark-1 ${
              locked ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
            }`
        }`}
      >
        {body}
      </motion.div>
    </Reorder.Item>
  );
}

export default memo(PlaylistItemTile);
